import pick from 'lodash/pick';
import { IntegrationType, type MasterTokenAdAccount } from 'types/integrations';

import {
  type AdAccount,
  AdAccountStatus,
  type AdAccountStore,
  allowedKeys,
  type MetaAdAccount
} from 'types/stores/ad-account-store';
import { create } from 'zustand';

const useAdAccountStore = create<AdAccountStore>((set, get) => ({
  metaAdAccount: null,
  tiktokAdAccount: null,
  clear: (type: IntegrationType) =>
    set(() => {
      let store: Partial<AdAccountStore> = {};

      if (type === IntegrationType.Facebook) {
        store = { metaAdAccount: null, metaAdAccountsList: [] };
      }

      if (type === IntegrationType.TikTok) {
        store = { tiktokAdAccount: null, tiktokAdAccountsList: [] };
      }

      return store;
    }),
  updateAdAccount: <T extends IntegrationType>(
    adAccount: Partial<T extends IntegrationType.Facebook ? MetaAdAccount : AdAccount>,
    type: T
  ) =>
    set(prevState => {
      const newAdAccount = pick(adAccount, allowedKeys);
      let store: Partial<AdAccountStore> = {};

      if (type === IntegrationType.Facebook) {
        store.metaAdAccount = { ...prevState.metaAdAccount, ...newAdAccount } as MetaAdAccount;
      }

      if (type === IntegrationType.TikTok) {
        store = {
          tiktokAdAccount: newAdAccount as unknown as AdAccount
        };
        if (prevState.tiktokAdAccount)
          store.tiktokAdAccount = { ...prevState.tiktokAdAccount, ...newAdAccount };
      }

      return store;
    }),
  // list values
  metaAdAccountsList: [],
  tiktokAdAccountsList: [],
  getActiveAdAccountFromList: <T>(type: T) => {
    type TypeToReturn =
      | (T extends IntegrationType.Facebook ? MetaAdAccount : AdAccount)
      | undefined;
    if (type === IntegrationType.Facebook)
      return get().metaAdAccountsList.at(0) ?? (undefined as TypeToReturn);

    if (type === IntegrationType.TikTok)
      return get().tiktokAdAccountsList.find(
        adAccount => adAccount.status === AdAccountStatus.Active
      ) as TypeToReturn;

    return undefined;
  },
  updateAdAcountsList: <T>(
    adAccountsList: (T extends IntegrationType.Facebook ? MetaAdAccount : AdAccount)[],
    type: T
  ) => {
    if (type === IntegrationType.Facebook)
      set({ metaAdAccountsList: adAccountsList as MetaAdAccount[] });
    if (type === IntegrationType.TikTok)
      set({ tiktokAdAccountsList: adAccountsList as AdAccount[] });
  },

  masterTokenAdAccountsList: [],
  clearMasterTokenAdAccounts: () => set({ masterTokenAdAccountsList: [] }),
  updateMasterTokenAdAccountsList: (adAccountsList: MasterTokenAdAccount[]) =>
    set({
      masterTokenAdAccountsList: adAccountsList.map(
        account =>
          ({
            ...account,
            adAccountId: account.adAccountId || account.accountId // Ensure adAccountId is set
          }) as MasterTokenAdAccount
      )
    })
}));

export default useAdAccountStore;
